// src/features/task/TaskAttachments.tsx
// Attachments section for the task detail page. Files uploaded at creation
// (or linked later) land as RELATION_TYPE_ATTACHMENT resource links on the
// task entity — the same data already fetched for the Right Rail's Resources
// tab via entityStore.loadResources(), so this reuses that observable state
// instead of firing a second request. Download links are resolved on demand
// via attachmentApi.GetDownloadUrl.

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, Alert } from '@react/components';
import { attachmentApi } from '@core/api';
import { entityStore } from '@/stores/EntityStore';
import { formatDateTime } from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

export const TaskAttachments = observer(() => {
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const attachments = entityStore.resources.filter((r) => r.relation_type === 'RELATION_TYPE_ATTACHMENT');
  const loading = entityStore.isLoadingResources;

  const download = async (attachmentId: string) => {
    setDownloadingId(attachmentId);
    setError(null);
    try {
      const res = await attachmentApi.GetDownloadUrl(attachmentId);
      const url = res?.download_url?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setError(errMsg(e, t('task.attachmentsPanel.downloadError')));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('task.attachments')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {error && <Alert variant="error">{error}</Alert>}

        {loading ? (
          <div className="h-10 animate-pulse rounded-md bg-muted" aria-busy="true" />
        ) : attachments.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('task.attachmentsPanel.empty')}</p>
        ) : (
          <div className="divide-y divide-border">
            {attachments.map((a) => {
              const id = a.entity?.id || '';
              return (
                <div key={id} className="py-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-foreground truncate">{a.title || id.substring(0, 8)}</p>
                      {a.linked_at && (
                        <p className="text-xs text-muted-foreground">{formatDateTime(a.linked_at)}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    isLoading={downloadingId === id}
                    disabled={!id}
                    onClick={() => download(id)}
                  >
                    {t('task.attachmentsPanel.download')}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default TaskAttachments;
